import { useMemo, useState } from 'react';
import type { AccountDto, CatalogItemKind } from '@hesabat/contracts';
import { api, UnauthorizedError } from './api';
import { formatMoney, formatQuantity, isZero, parseAmount } from './money';
import Wizard, { useWizardDraft, type WizardStep } from './Wizard';
import Hint from './Hint';

interface Props {
  companyId: string;
  accounts: readonly AccountDto[];
  inventoryEnabled: boolean;
  /** الرصيد الافتتاحى قيد مخزون — بصلاحية `journal.manual` كشاشة المخزون. */
  canPostOpening: boolean;
  /** رسالة خطأ الكتالوج نفسها (اسم مكرر، حساب غير صالح…). */
  describeError: (caught: unknown) => string;
  onCreated: () => void;
  onClose: () => void;
  onSessionLost: () => void;
}

interface ItemDraft {
  step: number;
  done: string[];
  nameAr: string;
  unit: string;
  defaultPrice: string;
  kind: CatalogItemKind;
  revenueAccountCode: string;
  inventoryAccountCode: string;
  cogsAccountCode: string;
  etaItemCode: string;
  etaItemType: '' | 'EGS' | 'GS1';
  etaUnitType: string;
  openingDate: string;
  openingQuantity: string;
  openingUnitCost: string;
  /** مفاتيح ثابتة للمحاولة نفسها: إعادة «إنهاء» بعد خطأ شبكة لا تُنشئ صنفين. */
  itemKey: string;
  openingKey: string;
  /** صنف أُنشئ وفشل رصيده الافتتاحى — الإعادة ترحّل الرصيد وحده. */
  createdItemId: string | null;
}

function freshDraft(stock: boolean): ItemDraft {
  return {
    step: 0,
    done: [],
    nameAr: '',
    unit: stock ? 'قطعة' : 'خدمة',
    defaultPrice: '',
    kind: stock ? 'stock' : 'service',
    revenueAccountCode: '',
    inventoryAccountCode: '',
    cogsAccountCode: '',
    etaItemCode: '',
    etaItemType: '',
    etaUnitType: '',
    openingDate: new Date().getFullYear() + '-01-01',
    openingQuantity: '',
    openingUnitCost: '',
    itemKey: crypto.randomUUID(),
    openingKey: crypto.randomUUID(),
    createdItemId: null,
  };
}

/** الرصيد الافتتاحى: فارغ تمامًا (تخطٍّ)، أو كمية موجبة غير صفرية وتكلفة صالحة. */
export function openingState(quantity: string, unitCost: string): 'empty' | 'ready' | 'invalid' {
  if (quantity.trim() === '' && unitCost.trim() === '') return 'empty';
  const parsedQuantity = parseAmount(quantity);
  const parsedCost = parseAmount(unitCost);
  // الحقل الفارغ يُقرأ صفرًا — والتكلفة المنسية ليست تكلفة صفرية، فتُطلب صراحة
  if (quantity.trim() === '' || unitCost.trim() === '') return 'invalid';
  if (parsedQuantity === null || isZero(parsedQuantity) || parsedCost === null) return 'invalid';
  return 'ready';
}

/**
 * صنف جديد بخطوات: البيانات ← الحسابات والفاتورة الإلكترونية ← المخزون الافتتاحى ← المراجعة.
 * يحفظ بنفس مسارى الكتالوج والرصيد الافتتاحى فى شاشة المخزون — لا قيد يُبنى هنا.
 */
export default function ItemWizard({
  companyId,
  accounts,
  inventoryEnabled,
  canPostOpening,
  describeError,
  onCreated,
  onClose,
  onSessionLost,
}: Props) {
  const [draft, setDraft, clearDraft, savedAt] = useWizardDraft<ItemDraft>(
    'hesabat.item-wizard.' + companyId,
    freshDraft(inventoryEnabled),
  );
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const revenueAccounts = accounts.filter(
    (account) => account.accountClass === 'revenue' && account.isPostable,
  );
  const inventoryAccounts = accounts.filter(
    (account) =>
      account.accountClass === 'asset' && account.subledger === 'inventory' && account.isPostable,
  );
  const cogsAccounts = accounts.filter(
    (account) => account.accountClass === 'cogs' && account.isPostable,
  );

  const stock = draft.kind === 'stock';
  const withOpening = stock && canPostOpening;
  const steps = useMemo(() => {
    const list: WizardStep[] = [
      {
        id: 'basics',
        title: 'البيانات',
        hint: 'الاسم والوحدة والسعر كما تظهر على الفاتورة.',
        icon: 'tag',
      },
      {
        id: 'accounts',
        title: 'الحسابات والمنظومة',
        hint: 'الافتراضى يكفى لأغلب الشركات — غيّره فقط إن طلب المحاسب.',
        icon: 'ledger',
      },
    ];
    if (withOpening) {
      list.push({
        id: 'opening',
        title: 'المخزون الافتتاحى',
        hint: 'الكمية الموجودة الآن وتكلفة الوحدة — اتركه فارغًا إن لم يوجد رصيد.',
        icon: 'box',
        optional: true,
      });
    }
    list.push({ id: 'review', title: 'المراجعة', icon: 'check' });
    return list;
  }, [withOpening]);

  const current = Math.min(draft.step, steps.length - 1);
  const step = steps[current]!;
  const price = parseAmount(draft.defaultPrice);
  const basicsReady = draft.nameAr.trim().length >= 2 && draft.unit.trim() !== '' && price !== null;
  const opening = withOpening
    ? openingState(draft.openingQuantity, draft.openingUnitCost)
    : 'empty';

  function update(change: Partial<ItemDraft>): void {
    setFailure(null);
    setDraft((value) => ({ ...value, ...change }));
  }

  function fail(caught: unknown): void {
    if (caught instanceof UnauthorizedError) onSessionLost();
    else setFailure(describeError(caught));
  }

  async function finish(): Promise<boolean> {
    if (!basicsReady || price === null || opening === 'invalid') return false;
    setBusy(true);
    setFailure(null);
    let itemId = draft.createdItemId;
    try {
      if (!itemId) {
        const item = await api.createCatalogItem(companyId, draft.itemKey, {
          nameAr: draft.nameAr.trim(),
          unit: draft.unit.trim(),
          defaultPrice: price,
          revenueAccountCode: draft.revenueAccountCode || null,
          etaItemCode: draft.etaItemCode.trim(),
          etaItemType: draft.etaItemType || null,
          etaUnitType: draft.etaUnitType.trim(),
          kind: draft.kind,
          inventoryAccountCode: stock ? draft.inventoryAccountCode || null : null,
          cogsAccountCode: stock ? draft.cogsAccountCode || null : null,
        });
        itemId = item.id;
        setDraft((value) => ({ ...value, createdItemId: item.id }));
        onCreated();
      }
      if (opening === 'ready') {
        await api.createStockOpening(companyId, draft.openingKey, {
          documentDate: draft.openingDate,
          lines: [
            {
              itemId,
              quantity: parseAmount(draft.openingQuantity)!,
              unitCost: parseAmount(draft.openingUnitCost)!,
            },
          ],
        });
        onCreated();
      }
      return true;
    } catch (caught) {
      fail(caught);
      return false;
    } finally {
      setBusy(false);
    }
  }

  const accountName = (code: string, fallback: string) =>
    code
      ? code + ' — ' + (accounts.find((account) => account.code === code)?.nameAr ?? '')
      : fallback;

  return (
    <Wizard
      title="صنف جديد"
      intro="خطوة بخطوة، والمسودة محفوظة لو خرجت."
      steps={steps}
      current={current}
      onStep={(index) => setDraft((value) => ({ ...value, step: index }))}
      completed={new Set(draft.done)}
      canNext={
        step.id === 'basics' ? basicsReady : step.id === 'opening' ? opening !== 'invalid' : true
      }
      onNext={async () => {
        if (step.id === 'review') return finish();
        setDraft((value) => ({
          ...value,
          done: value.done.includes(step.id) ? value.done : [...value.done, step.id],
        }));
        return true;
      }}
      onFinish={() => {
        clearDraft();
        onClose();
      }}
      finishLabel={draft.createdItemId ? 'ترحيل الرصيد الافتتاحى' : 'إضافة الصنف'}
      busy={busy}
      savedAt={savedAt}
    >
      {failure && <p className="error">{failure}</p>}

      {step.id === 'basics' && (
        <div className="form-row">
          <label className="grow">
            الاسم
            <input
              value={draft.nameAr}
              maxLength={200}
              onChange={(event) => update({ nameAr: event.target.value })}
            />
          </label>
          <label>
            الوحدة
            <input
              value={draft.unit}
              maxLength={32}
              onChange={(event) => update({ unit: event.target.value })}
            />
            <Hint>كيف تبيعه: قطعة، كيلو، متر، ساعة، علبة.</Hint>
          </label>
          <label>
            سعر البيع الافتراضى
            <input
              inputMode="decimal"
              value={draft.defaultPrice}
              aria-invalid={draft.defaultPrice !== '' && price === null}
              onChange={(event) => update({ defaultPrice: event.target.value })}
            />
            <Hint>يُكتب تلقائيًا فى الفاتورة، ويمكنك تغييره فيها.</Hint>
          </label>
          {inventoryEnabled && (
            <label>
              النوع
              <select
                value={draft.kind}
                disabled={draft.createdItemId !== null}
                onChange={(event) => update({ kind: event.target.value as CatalogItemKind })}
              >
                <option value="stock">صنف مخزنى (يُتابع رصيده)</option>
                <option value="service">خدمة (بلا رصيد)</option>
              </select>
              <Hint>
                «مخزنى» لو تشتريه وتخزّنه فيتابع البرنامج كميته، و«خدمة» لو لا كمية له (صيانة،
                استشارة، نقل).
              </Hint>
            </label>
          )}
        </div>
      )}

      {step.id === 'accounts' && (
        <>
          <div className="form-row">
            <label className="grow">
              حساب الإيراد
              <select
                value={draft.revenueAccountCode}
                onChange={(event) => update({ revenueAccountCode: event.target.value })}
              >
                <option value="">الافتراضى (إيرادات الخدمات)</option>
                {revenueAccounts.map((account) => (
                  <option key={account.code} value={account.code}>
                    {account.code} — {account.nameAr}
                  </option>
                ))}
              </select>
              <Hint>
                أين يُسجَّل دخل بيع هذا الصنف. اترك «الافتراضى» إن لم تكن متأكدًا — هو الصحيح لمعظم
                الشركات.
              </Hint>
            </label>
            {stock && (
              <>
                <label className="grow">
                  حساب المخزون
                  <select
                    value={draft.inventoryAccountCode}
                    onChange={(event) => update({ inventoryAccountCode: event.target.value })}
                  >
                    <option value="">الافتراضى (مخزون بضاعة 114001)</option>
                    {inventoryAccounts.map((account) => (
                      <option key={account.code} value={account.code}>
                        {account.code} — {account.nameAr}
                      </option>
                    ))}
                  </select>
                  <Hint>
                    أين تُسجَّل قيمة البضاعة الموجودة عندك فى المخزن. اترك «الافتراضى» إن لم تكن
                    متأكدًا — هو الصحيح لمعظم الشركات.
                  </Hint>
                </label>
                <label className="grow">
                  حساب تكلفة المبيعات
                  <select
                    value={draft.cogsAccountCode}
                    onChange={(event) => update({ cogsAccountCode: event.target.value })}
                  >
                    <option value="">الافتراضى (تكلفة بضاعة مباعة 511001)</option>
                    {cogsAccounts.map((account) => (
                      <option key={account.code} value={account.code}>
                        {account.code} — {account.nameAr}
                      </option>
                    ))}
                  </select>
                  <Hint>
                    أين تُسجَّل تكلفة البضاعة لحظة بيعها، ليظهر ربحك الحقيقى. اترك «الافتراضى» إن لم
                    تكن متأكدًا — هو الصحيح لمعظم الشركات.
                  </Hint>
                </label>
              </>
            )}
          </div>
          <div className="form-row">
            <label>
              كود الفاتورة الإلكترونية (اختيارى)
              <input
                value={draft.etaItemCode}
                maxLength={64}
                onChange={(event) => update({ etaItemCode: event.target.value })}
              />
              <Hint>مطلوب فقط لو تُصدر فواتير إلكترونية: كود الصنف المسجّل فى منظومة الضرائب.</Hint>
            </label>
            <label>
              نوع الكود
              <select
                value={draft.etaItemType}
                onChange={(event) =>
                  update({ etaItemType: event.target.value as ItemDraft['etaItemType'] })
                }
              >
                <option value="">— غير محدد —</option>
                <option value="EGS">EGS (كود داخلى مسجّل فى المنظومة)</option>
                <option value="GS1">GS1 (باركود عالمى)</option>
              </select>
              <Hint>
                GS1 لو على الصنف باركود عالمى، وEGS لو كود خاص بشركتك سجّلته فى منظومة الضرائب.
              </Hint>
            </label>
            <label>
              كود الوحدة فى المنظومة (مثل EA)
              <input
                value={draft.etaUnitType}
                maxLength={16}
                onChange={(event) => update({ etaUnitType: event.target.value })}
              />
              <Hint>رمز الوحدة عند الضرائب: EA للقطعة، KGM للكيلو. اسأل محاسبك إن لم تعرفه.</Hint>
            </label>
          </div>
          {stock && (
            <p className="sub">
              الحسابان الافتراضيان من الدليل المرجعى ينتظران اعتماد المحاسب. بعد أول حركة لا يتغير
              نوع الصنف ولا حساب مخزونه.
            </p>
          )}
        </>
      )}

      {step.id === 'opening' && (
        <>
          <div className="form-row">
            <label>
              التاريخ
              <input
                type="date"
                value={draft.openingDate}
                onChange={(event) => update({ openingDate: event.target.value })}
              />
              <Hint>اليوم الذى كانت فيه هذه الكمية عندك — عادةً يوم بدء العمل على البرنامج.</Hint>
            </label>
            <label>
              الكمية
              <input
                inputMode="decimal"
                value={draft.openingQuantity}
                aria-invalid={opening === 'invalid'}
                onChange={(event) => update({ openingQuantity: event.target.value })}
              />
              <Hint>كم قطعة (أو كيلو…) عندك الآن من هذا الصنف فى المخزن.</Hint>
            </label>
            <label>
              تكلفة الوحدة
              <input
                inputMode="decimal"
                value={draft.openingUnitCost}
                aria-invalid={opening === 'invalid'}
                onChange={(event) => update({ openingUnitCost: event.target.value })}
              />
              <Hint>كم دفعت فى الوحدة الواحدة: سعر الشراء، لا سعر البيع.</Hint>
            </label>
          </div>
          {opening === 'invalid' && (
            <p className="error">أدخل الكمية (غير صفر) وتكلفة الوحدة معًا، أو اتركهما فارغين.</p>
          )}
          <p className="sub">
            المقابل حساب فروق الأرصدة الافتتاحية (أو الأرباح المرحلة حسب الإعدادات) كما فى شاشة
            المخزون — لا تُدخل هذا المخزون مرة ثانية فى قيد الافتتاح العام.
          </p>
        </>
      )}

      {step.id === 'review' && draft.createdItemId && (
        <p className="notice">أُضيف الصنف — بقى ترحيل رصيده الافتتاحى.</p>
      )}
      {step.id === 'review' && (
        <dl className="wizard-review">
          <div>
            <dt>الصنف</dt>
            <dd>
              {draft.nameAr} — {draft.unit}
            </dd>
          </div>
          <div>
            <dt>سعر البيع</dt>
            <dd>{price ? formatMoney(price) : '—'}</dd>
          </div>
          <div>
            <dt>النوع</dt>
            <dd>{stock ? 'صنف مخزنى' : 'خدمة'}</dd>
          </div>
          <div>
            <dt>حساب الإيراد</dt>
            <dd>{accountName(draft.revenueAccountCode, 'الافتراضى')}</dd>
          </div>
          {stock && (
            <div>
              <dt>حساب المخزون</dt>
              <dd>{accountName(draft.inventoryAccountCode, 'الافتراضى (114001)')}</dd>
            </div>
          )}
          {withOpening && (
            <div>
              <dt>الرصيد الافتتاحى</dt>
              <dd>
                {opening === 'ready'
                  ? formatQuantity(parseAmount(draft.openingQuantity)!) +
                    ' × ' +
                    formatMoney(parseAmount(draft.openingUnitCost)!) +
                    ' بتاريخ ' +
                    draft.openingDate
                  : 'لا رصيد'}
              </dd>
            </div>
          )}
        </dl>
      )}
    </Wizard>
  );
}
